import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { leadService, getErrorMessage } from '@/services';
import type { LeadFilters, LeadActivity } from '@/types';
import type { LeadWritePayload } from '@/services/leads';

export function useLeads(page: number = 1, filters?: LeadFilters) {
  return useQuery({
    queryKey: ['leads', page, filters],
    queryFn: () => leadService.getLeads(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadService.getLead(id),
    enabled: !!id,
  });
}

export function useLeadActivities(leadId: string) {
  return useQuery({
    queryKey: ['lead-activities', leadId],
    queryFn: () => leadService.getActivities(leadId),
    enabled: !!leadId,
  });
}

export function useLeadSources() {
  return useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => leadService.getSources(),
  });
}

export function useLeadCampaigns() {
  return useQuery({
    queryKey: ['lead-campaigns'],
    queryFn: () => leadService.getCampaigns(),
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LeadWritePayload) => leadService.createLead(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LeadWritePayload> }) =>
      leadService.updateLead(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
      toast.success('Lead updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      leadService.updateLeadStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['lead-activities', variables.id] });
      toast.success('Lead stage updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leadService.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Lead deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useAssignLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId, notes }: { id: string; userId: string; notes?: string }) =>
      leadService.assignLead(id, userId, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
      // The assignment is written to the lead's timeline server-side, so the
      // activity feed has to be refetched too or it shows a stale history.
      queryClient.invalidateQueries({ queryKey: ['lead-activities', variables.id] });
      toast.success('Lead assigned successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useConvertLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => leadService.convertLead(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      // The customer list gained a row (or an existing one gained a lead), so
      // it is stale either way.
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      // "Converted successfully" hid the difference that matters: whether a
      // NEW customer exists or the lead was folded into one that already did.
      toast.success(
        result.customer_created ? 'Customer created from lead' : 'Linked to existing customer'
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** #273: bulk convert. Resolves with the per-lead outcome; the page renders it. */
export function useBulkConvertLeads() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadIds: string[]) => leadService.bulkConvertLeads(leadIds),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      const converted = result.converted.length;
      const skipped = result.skipped.length;
      if (converted === 0) {
        toast.error(`No leads converted (${skipped} skipped)`);
      } else if (skipped > 0) {
        toast.success(`${converted} converted, ${skipped} skipped`);
      } else {
        toast.success(`${converted} lead${converted === 1 ? '' : 's'} converted to customers`);
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useAddLeadActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, data }: { leadId: string; data: Partial<LeadActivity> }) =>
      leadService.addActivity(leadId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lead-activities', variables.leadId] });
      toast.success('Activity added successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Who a lead may be assigned to — this broker's own staff, per the server.
 * Pass `enabled: false` for a role that cannot assign (a POSP): the server answers 403 and
 * the console and network log would show a failing call on every open (R4-F7, #612).
 */
export function useAssignableUsers(enabled = true) {
  return useQuery({
    queryKey: ['lead-assignable-users'],
    queryFn: () => leadService.getAssignableUsers(),
    enabled,
  });
}

export function useBulkAssignLeads() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ leadIds, assignedTo }: { leadIds: string[]; assignedTo: string }) =>
      leadService.bulkAssignLeads(leadIds, assignedTo),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      // The SERVER's count, not the number requested: they differ when a lead
      // has moved out of scope since the page was rendered.
      toast.success(
        `${result.assigned_count} lead${result.assigned_count === 1 ? '' : 's'} assigned`,
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}
